import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteContent, updateContentRanking } from '../services/requests';
import useNotification from '../components/Notification';
import { getRankUpdatesAfterDeletion } from '../helpers/rankUtils';
import type { RankingItem } from '../schemas/auth';

interface UseContentDeleteOptions {
  onSuccess?: () => void;
  onRankUpdate?: (rankUpdates: RankingItem[]) => void;
}

export const useContentDelete = (options?: UseContentDeleteOptions) => {
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, deletedRank, currentItems }: { id: string; deletedRank?: number; currentItems?: { id: string; rank: number }[] }) => {
      const result = await deleteContent(id);

      if (deletedRank && currentItems) {
        const rankUpdates = getRankUpdatesAfterDeletion(deletedRank, currentItems);

        if (rankUpdates.length > 0) {
          try {
            await updateContentRanking(rankUpdates);
            if (options?.onRankUpdate) {
              options.onRankUpdate(rankUpdates);
            }
          } catch (error) {
            console.error('Failed to update ranks after deletion:', error);
          }
        }
      }

      return result;
    },
    onSuccess: (data) => {
      showNotification({
        type: 'success',
        message: 'Content Deleted',
        description: 'The content has been deleted successfully.',
      });

      queryClient.invalidateQueries({
        queryKey: ['listContents'],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['infiniteContents'],
        exact: false,
      });

      if (options?.onSuccess) {
        options.onSuccess();
      }
    },
  });
};
